// DesktopHome.jsx — multi-panel home view for the desktop web app.
//
// Activated by App.jsx whenever `isDesktop && tab === 'home'`. Replaces the
// mobile-styled Dashboard scene (which is a single-column phone-first habit
// tracker) with a wide window-shaped layout the user explicitly asked for:
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │ TUE · APR 21, 2026                  [ search ] [bell] [ + ]  │
//   │ The Consistency Dashboard                                    │
//   ├──────────────────────────────────────────────────────────────┤
//   │ [Completed 33] [Remaining 57] [Progress 37%] [Day Streak 4]  │
//   ├─────────────────────────────────────────┬────────────────────┤
//   │ April 2026          Jan Feb Mar [Apr]   │  Today's brief     │
//   │                                         │   FOCUS card       │
//   │ HABIT    M15 T16 W17 T18 F19 S20 S21    │   Now playing      │
//   │ Gym      ✓   ✓   ✓   ✓   ✓   ✓   ✓     │   Finance brief    │
//   │ Read     ✓   ✓   ·   ✓   ✓   ✓   ✓     │   Science brief    │
//   │ ...                                      │                    │
//   └─────────────────────────────────────────┴────────────────────┘
//
// All numbers are derived from the same TanStack Query data the mobile
// Dashboard reads, so the two views stay in lockstep without duplicating
// state. The wider habit-by-date matrix is rendered inline here rather than
// importing the mobile Dashboard's existing matrix — the mobile component
// has month-wide widths and won't compress cleanly into the 7-day strip.

import { useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '../components/Icon.jsx';
import { useHabits, useCheckins, useToggleCheckin, useBriefing } from '../lib/hooks.js';
import { briefStore } from '../lib/briefStore.js';
import { bundleMeta } from '../lib/briefBundles.js';
import { todayIsoKey, anchoredToday } from '../lib/dateKeys.js';
import { sfx } from '../lib/sfx.js';
import * as audioPlayerStore from '../lib/audioPlayerStore.js';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAY_SHORT = ['S','M','T','W','T','F','S'];

// Build a 7-day strip for the given month. The strip ends on today's
// date if the selected month IS the current month, otherwise on the
// last day of that month — so past months always show their final week
// and the current month rolls forward as days accumulate.
function buildDateStrip(year, month, n = 7) {
  const todayIso = todayIsoKey();
  const today = anchoredToday();
  // Anchor: the last day we want to display in the strip.
  const isCurrentMonth = year === today.year && month === today.month;
  const anchor = isCurrentMonth
    ? new Date(today.year, today.month, today.day)
    : new Date(year, month + 1, 0); // day 0 of next month = last day of this month
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(anchor);
    d.setDate(d.getDate() - i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    out.push({
      iso,
      dayNum: d.getDate(),
      weekday: WEEKDAY_SHORT[d.getDay()],
      isToday: iso === todayIso,
    });
  }
  return out;
}

// Format a Date as "TUE · APR 21, 2026" for the page header eyebrow. Mirrors
// the typographic style of the marketing screenshot (uppercase, dot-spaced).
function formatHeaderDate(date = new Date()) {
  const dow = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const day = date.getDate();
  const year = date.getFullYear();
  return `${dow} · ${month} ${day}, ${year}`;
}

// Subscribe to briefStore so the right rail refreshes when a brief is
// generated in another tab. localStorage doesn't fire `storage` events on
// the same tab that wrote, hence the polling fallback.
function useBriefList() {
  const [briefs, setBriefs] = useState(() => briefStore.listBriefs());
  useEffect(() => {
    setBriefs(briefStore.listBriefs());
    const onStorage = (e) => {
      if (!e || e.key == null || e.key.includes('brief')) {
        setBriefs(briefStore.listBriefs());
      }
    };
    window.addEventListener('storage', onStorage);
    const iv = setInterval(() => setBriefs(briefStore.listBriefs()), 5000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(iv);
    };
  }, []);
  return briefs;
}

// Subscribe to the audio player store so the Now Playing card reflects the
// currently-playing brief (or hides itself if nothing's playing).
function useNowPlaying() {
  const [state, setState] = useState(() => audioPlayerStore.getState());
  useEffect(() => audioPlayerStore.subscribe(setState), []);
  return state;
}

export function DesktopHome({ onOpenAdd, onSwitchTab }) {
  const { data: habits = [] } = useHabits();
  const { data: checkins = [] } = useCheckins();
  const toggleCheckin = useToggleCheckin();
  const todayBriefing = useBriefing();
  const briefs = useBriefList();
  const nowPlaying = useNowPlaying();

  // Fast O(1) check-in lookup. `${habitId}|${iso}` → 'done'.
  const doneSet = useMemo(() => {
    const s = new Set();
    for (const c of checkins) if (c.status === 'done') s.add(`${c.habitId}|${c.date}`);
    return s;
  }, [checkins]);

  // Selected month for the habit matrix. Defaults to today's month;
  // clicking a month pill in the panel header updates it. The date
  // strip is recomputed when this changes.
  const today = anchoredToday();
  const [selected, setSelected] = useState({ year: today.year, month: today.month });
  const dateStrip = useMemo(
    () => buildDateStrip(selected.year, selected.month, 7),
    [selected.year, selected.month]
  );
  const todayIso = todayIsoKey();

  // ── KPI math ───────────────────────────────────────────────────
  // "Completed" = total check-ins all-time across all habits. "Remaining" =
  // habits × days-since-creation that haven't been ticked. "Progress" =
  // completed / (completed + remaining). "Day streak" = the maximum
  // streak across all active habits (matches what the mobile Dashboard
  // surfaces on its hero card so the numbers feel consistent).
  const kpi = useMemo(() => {
    const completed = checkins.filter(c => c.status === 'done').length;
    // Cheap remaining heuristic: for each habit, count days from creation
    // (or 30 days back, whichever is shorter) that are not checked off.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let remaining = 0;
    for (const h of habits) {
      const created = h.createdAt ? new Date(h.createdAt) : new Date(today.getTime() - 30 * 86400000);
      const days = Math.min(30, Math.max(1, Math.floor((today - created) / 86400000) + 1));
      remaining += Math.max(0, days - (h.checkinsCount || 0));
    }
    const total = completed + remaining;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    const streak = habits.reduce((m, h) => Math.max(m, h.streak || 0), 0);
    return { completed, remaining, progress, streak };
  }, [habits, checkins]);

  const onToggleCell = (habit, iso) => {
    const isDone = doneSet.has(`${habit.id}|${iso}`);
    const next = !isDone;
    next ? sfx.check && sfx.check() : sfx.uncheck && sfx.uncheck();
    toggleCheckin.mutate({ habitId: habit.id, isoDate: iso, next });
  };

  // Horizontal-wheel → month navigation.
  //
  // Mac trackpads and tilt-wheel mice deliver horizontal scroll as
  // `event.deltaX`. Browsers also map shift+wheel to deltaX. We
  // accumulate raw delta into a ref and step `selected.month` whenever
  // the accumulator crosses a threshold; vertical scroll (deltaY) is
  // ignored so the page can still scroll normally. Bounds: 0..11 within
  // the current year — going past December or before January is a no-op
  // (we don't yet support year navigation; the rest of the dashboard
  // doesn't either).
  const wheelAccumRef = useRef(0);
  const lastWheelStepRef = useRef(0);
  const onMatrixWheel = (e) => {
    // Only act when the dominant axis is horizontal, OR shift is held.
    // This avoids hijacking ordinary vertical scrolling.
    const dx = e.deltaX;
    const dy = e.deltaY;
    const horizontal = Math.abs(dx) > Math.abs(dy) || e.shiftKey;
    if (!horizontal) return;
    const delta = e.shiftKey && Math.abs(dx) < 1 ? dy : dx;
    if (delta === 0) return;
    e.preventDefault();
    wheelAccumRef.current += delta;
    // Threshold is intentionally generous so a single firm swipe ≈ one
    // month and a slow drift doesn't fire repeatedly.
    const STEP = 80;
    const now = performance.now();
    if (Math.abs(wheelAccumRef.current) >= STEP && now - lastWheelStepRef.current > 220) {
      const dir = wheelAccumRef.current > 0 ? 1 : -1;
      setSelected(prev => {
        const next = Math.max(0, Math.min(11, prev.month + dir));
        return { year: prev.year, month: next };
      });
      wheelAccumRef.current = 0;
      lastWheelStepRef.current = now;
      sfx.month && sfx.month();
    }
  };

  // Label reflects whichever month the user has pinned in the strip.
  const monthLabel = `${MONTH_NAMES[selected.month]} ${selected.year}`;

  const latestBrief = briefs[0] || null;
  const playingBrief = nowPlaying?.brief || null;

  return (
    <div className="dh">
      {/* ── Page header ──────────────────────────────────────── */}
      <div className="dh-pagehead">
        <div className="dh-pagehead-l">
          <div className="dh-eyebrow">{formatHeaderDate()}</div>
          <h1 className="dh-title">
            The <em>Consistency</em> Dashboard
          </h1>
        </div>
        <div className="dh-pagehead-r">
          <div className="dh-search">
            <Icon name="search" size={16}/>
            <input type="text" placeholder="Search habits, briefs, agenda…" aria-label="Search"/>
          </div>
          <button className="dh-icon-btn" aria-label="Notifications">
            <Icon name="bell" size={16}/>
          </button>
          {/* The "Add habit" plus button used to live here in the header,
              next to bell/search — but it's far from the habit list and
              read as "create something new in general" rather than "add a
              habit." Moved to a dedicated row at the bottom of the habit
              matrix where the affordance is unambiguous. */}
        </div>
      </div>

      {/* ── KPI bar ──────────────────────────────────────────── */}
      <div className="dh-kpi-bar">
        <KpiCard label="Completed" value={kpi.completed} icon="check"/>
        <KpiCard label="Remaining" value={kpi.remaining} icon="target"/>
        <KpiCard label="Progress" value={`${kpi.progress}%`} icon="chart" accent/>
        <KpiCard label="Day Streak" value={kpi.streak} icon="zap"/>
      </div>

      {/* ── Two-column body ──────────────────────────────────── */}
      <div className="dh-body">
        {/* ── Habits matrix (left) ───────────────────────────── */}
        <section className="dh-panel">
          <header className="dh-panel-head">
            <h2 className="dh-panel-title">{monthLabel}</h2>
            <div className="dh-panel-head-r">
              {/* Month-pill strip — a rolling 5-month window centered on
                  today's month. Buggy older logic hard-coded Jan..May and
                  lit up multiple pills when "today.month % 5" matched more
                  than one slot; the rolling-window approach makes the
                  "current" pill exactly one slot every time, regardless of
                  what month it is. */}
              <div className="dh-month-pills">
                {(() => {
                  // Center today's month in the middle of a 5-pill strip.
                  // Pills are buttons so the user can navigate back to
                  // earlier months (and forward, but the date strip will
                  // still cap at today for the current month — there are
                  // no future cells in the matrix). Selecting a past
                  // month shows that month's final week in the strip.
                  const start = Math.max(0, Math.min(today.month - 2, 12 - 5));
                  return Array.from({ length: 5 }, (_, i) => {
                    const m = start + i;
                    return (
                      <button
                        type="button"
                        key={m}
                        className={'dh-month-pill' + (m === selected.month ? ' on' : '')}
                        onClick={() => setSelected({ year: today.year, month: m })}
                        aria-pressed={m === selected.month}
                      >
                        {MONTH_NAMES[m].slice(0, 3)}
                      </button>
                    );
                  });
                })()}
              </div>
              {/* Compact + button — replaces the dashed "+ Add habit" row
                  that used to sit at the bottom of the matrix. That row
                  was the visible thing the user saw "popping up" while
                  scrolling: it slid into view as you scrolled to the
                  bottom and read as an unwanted modal trigger. Moving
                  the affordance into the always-visible header removes
                  it from the scroll path entirely while keeping a
                  dedicated plus button for adding habits. */}
              <button
                type="button"
                className="dh-add-btn"
                onClick={onOpenAdd}
                title="Add habit"
                aria-label="Add habit"
              >
                <Icon name="plus" size={16}/>
              </button>
            </div>
          </header>

          {habits.length === 0 ? (
            <div className="dh-empty">
              <p>No habits yet.</p>
              <button className="dh-empty-cta" onClick={onOpenAdd}>+ Add your first habit</button>
            </div>
          ) : (
            // Grid columns: habit-name | per-day cells | per-row %
            // The trailing 56px column shows each habit's completion rate
            // across the visible date strip. A summary row appended after
            // all habits shows the per-day completion rate as a column
            // total. User asked for "percentages on the left and right"
            // and "expand vertically, no internal scroll" — done.
            <div className="dh-matrix" onWheel={onMatrixWheel} style={{ gridTemplateColumns: `160px repeat(${dateStrip.length}, 1fr) 56px` }}>
              <div className="dh-matrix-col-head">HABIT</div>
              {dateStrip.map(d => (
                <div key={d.iso} className={'dh-matrix-col-head dh-matrix-date' + (d.isToday ? ' on' : '')}>
                  <small>{d.dayNum}</small>
                  <b>{d.weekday}</b>
                </div>
              ))}
              <div className="dh-matrix-col-head dh-matrix-pct-head">%</div>

              {habits.map(h => (
                <Row key={h.id} habit={h} dateStrip={dateStrip} doneSet={doneSet} onToggle={onToggleCell} todayIso={todayIso}/>
              ))}

              {/* Per-day completion summary row */}
              <div className="dh-matrix-row-head dh-matrix-summary-head">All habits</div>
              {dateStrip.map(d => {
                const total = habits.length;
                const done = habits.filter(h => doneSet.has(`${h.id}|${d.iso}`)).length;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={d.iso} className="dh-matrix-day-pct">
                    <span>{pct}%</span>
                  </div>
                );
              })}
              <div className="dh-matrix-day-pct dh-matrix-corner"></div>

              {/* The dashed "+ Add habit" row used to sit here and was
                  what the user perceived as the modal "popping up" while
                  scrolling — it slid into view at the bottom of the
                  matrix. The plus button now lives in the panel header
                  (always visible, fixed position) so adding a habit is
                  an intentional click, never a side-effect of scrolling. */}
            </div>
          )}
        </section>

        {/* ── Today's brief rail (right) ─────────────────────── */}
        <aside className="dh-rail">
          <header className="dh-rail-head">
            <h2 className="dh-panel-title">Today's brief</h2>
            <span className="dh-rail-meta">
              {latestBrief ? `${(bundleMeta(latestBrief.bundle)?.label || latestBrief.bundle || 'Brief').toUpperCase()} · ${formatBriefDate(latestBrief.generatedAt)}` : 'No briefs yet'}
            </span>
          </header>

          {/* FOCUS — pulled from today's briefing's focus field if set. */}
          {todayBriefing?.data?.focus && (
            <div className="dh-focus">
              <div className="dh-focus-label">
                <Icon name="target" size={14}/>
                <span>FOCUS</span>
              </div>
              <p>{todayBriefing.data.focus}</p>
            </div>
          )}

          {/* Now Playing — only if audio is actually playing. */}
          {playingBrief && (
            <div className="dh-now">
              <button
                className="dh-now-play"
                onClick={() => audioPlayerStore.toggle()}
                aria-label={nowPlaying.isPlaying ? 'Pause' : 'Play'}
              >
                <Icon name={nowPlaying.isPlaying ? 'pause' : 'play'} size={18}/>
              </button>
              <div className="dh-now-info">
                <small>Now playing</small>
                <b>{(playingBrief.headline || bundleMeta(playingBrief.bundle)?.label || 'Brief')}</b>
                <div className="dh-now-prog">
                  <i style={{ width: `${Math.min(100, Math.max(0, (nowPlaying.progress || 0) * 100))}%` }}/>
                </div>
              </div>
            </div>
          )}

          {/* Brief list — up to 4 most recent. Clicking one flips the
              app to the Intelligence tab with that brief preselected. */}
          {briefs.slice(0, 4).map(b => (
            <BriefCard key={b.id} brief={b} onOpen={() => onSwitchTab && onSwitchTab('intelligence', b.id)}/>
          ))}

          {briefs.length === 0 && (
            <div className="dh-rail-empty">
              <p>No briefs yet. Generate your first one from the Intel Brief tab.</p>
              <button onClick={() => onSwitchTab && onSwitchTab('intelligence')}>Open Intel Brief</button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────

function KpiCard({ label, value, icon, accent }) {
  return (
    <div className={'dh-kpi' + (accent ? ' dh-kpi--accent' : '')}>
      <div className="dh-kpi-l">
        <small>{label.toUpperCase()}</small>
        <b>{value}</b>
      </div>
      <span className="dh-kpi-icon">
        <Icon name={icon} size={18}/>
      </span>
    </div>
  );
}

function Row({ habit, dateStrip, doneSet, onToggle, todayIso }) {
  // Per-habit completion % across the visible date strip — counts only
  // cells in the past (future cells are excluded so the rate isn't
  // diluted by unreachable days).
  const reachable = dateStrip.filter(d => d.iso <= todayIso);
  const done = reachable.filter(d => doneSet.has(`${habit.id}|${d.iso}`)).length;
  const pct = reachable.length > 0 ? Math.round((done / reachable.length) * 100) : 0;

  return (
    <>
      <div className="dh-matrix-row-head">
        <span className="dh-habit-icon" style={{
          background: habit.color ? `${habit.color}1A` : 'rgba(45,156,255,0.12)',
          color: habit.color || 'var(--accent)',
        }}>
          <Icon name={habit.icon || 'target'} size={14}/>
        </span>
        <span className="dh-habit-name">{habit.name}</span>
      </div>
      {dateStrip.map(d => {
        const isDone = doneSet.has(`${habit.id}|${d.iso}`);
        const isFuture = d.iso > todayIso;
        return (
          <button
            key={d.iso}
            className={'dh-cell' + (isDone ? ' on' : '') + (isFuture ? ' future' : '')}
            disabled={isFuture}
            onClick={() => !isFuture && onToggle(habit, d.iso)}
            aria-label={`${habit.name} ${d.iso} ${isDone ? 'done' : 'not done'}`}
          >
            {isDone && <Icon name="check" size={14}/>}
          </button>
        );
      })}
      <div className="dh-row-pct">{pct}%</div>
    </>
  );
}

function BriefCard({ brief, onOpen }) {
  const meta = bundleMeta(brief.bundle) || {};
  const dateStr = formatBriefDate(brief.generatedAt);
  // Headlines on briefs vary by schema version — pull the first available
  // string-ish field. Falls back to bundle label if nothing's there.
  const headline =
    brief.headline ||
    brief.title ||
    (Array.isArray(brief.stories) && brief.stories[0]?.headline) ||
    (Array.isArray(brief.insights) && brief.insights[0]?.headline) ||
    (meta.label ? `${meta.label} brief` : 'Brief');
  const insightsCount =
    (Array.isArray(brief.insights) && brief.insights.length) ||
    (Array.isArray(brief.stories) && brief.stories.length) || null;

  return (
    <button className="dh-brief" onClick={onOpen}>
      <div className="dh-brief-head">
        <span className="dh-brief-bundle" style={{ color: meta.color || 'var(--accent)' }}>
          ● {(meta.label || brief.bundle || 'Brief').toUpperCase()}
        </span>
        <span className="dh-brief-date">{dateStr}</span>
      </div>
      <h4>{headline}</h4>
      {insightsCount && <small className="dh-brief-foot">Edition · {insightsCount} insights</small>}
    </button>
  );
}

function formatBriefDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
